import { apiPost } from '../api';
import { startQrScanner } from '../components/qr-scanner';

/** Render the first-run connect page. */
export function renderConnect(container: HTMLElement, onConnected: () => void): void {
  container.innerHTML = `
    <div class="connect-page">
      <h1 class="connect-title">Connect to your Hub</h1>
      <p class="connect-subtitle">Enter your hub's address to get started</p>

      <div class="connect-tabs">
        <button class="tab-btn active" data-tab="url">Enter URL</button>
        <button class="tab-btn" data-tab="qr">Scan QR</button>
        <button class="tab-btn" data-tab="seed">Seed Phrase</button>
      </div>

      <div class="connect-panel" id="panel-url">
        <label class="input-label" for="hub-url">Hub URL</label>
        <input type="url" id="hub-url" class="input" placeholder="hub.local, 192.168.1.50:8080, or abc123.cfargotunnel.com" />
        <label class="input-label" for="hub-token">Hub Token</label>
        <input type="password" id="hub-token" class="input" placeholder="Paste your hub master token" />
        <label class="input-label" for="hub-name">Name (optional)</label>
        <input type="text" id="hub-name" class="input" placeholder="Home Hub" />
        <button id="btn-connect-url" class="btn-primary">Connect</button>
      </div>

      <div class="connect-panel hidden" id="panel-qr">
        <div id="qr-viewport" class="qr-viewport">
          <p>Camera access required for QR scanning</p>
          <button id="btn-start-qr" class="btn-secondary">Open Camera</button>
        </div>
      </div>

      <div class="connect-panel hidden" id="panel-seed">
        <label class="input-label" for="seed-phrase">12-word Seed Phrase</label>
        <textarea id="seed-phrase" class="input textarea" rows="3" placeholder="word1 word2 word3 ..."></textarea>
        <button id="btn-connect-seed" class="btn-primary">Connect</button>
      </div>

      <p id="connect-error" class="error-text hidden"></p>
      <p id="connect-status" class="status-text hidden"></p>
    </div>
  `;

  // Tab switching
  const tabs = container.querySelectorAll<HTMLButtonElement>('.tab-btn');
  const panels = container.querySelectorAll<HTMLElement>('.connect-panel');
  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      tabs.forEach((t) => t.classList.remove('active'));
      tab.classList.add('active');
      const target = tab.dataset['tab'] ?? 'url';
      panels.forEach((p) => {
        p.classList.toggle('hidden', p.id !== `panel-${target}`);
      });
    });
  });

  // URL connect
  const btnUrl = container.querySelector<HTMLButtonElement>('#btn-connect-url');
  btnUrl?.addEventListener('click', async () => {
    const urlInput = container.querySelector<HTMLInputElement>('#hub-url');
    const tokenInput = container.querySelector<HTMLInputElement>('#hub-token');
    const nameInput = container.querySelector<HTMLInputElement>('#hub-name');
    const url = urlInput?.value.trim() ?? '';
    const token = tokenInput?.value.trim() ?? '';
    const name = nameInput?.value.trim() || url;
    if (!url) {
      showError(container, 'Please enter a hub URL');
      return;
    }
    if (!token) {
      showError(container, 'Please enter your hub token');
      return;
    }
    await connectHub(container, { name, url, token }, onConnected);
  });

  // QR scanner
  const btnQr = container.querySelector<HTMLButtonElement>('#btn-start-qr');
  btnQr?.addEventListener('click', () => {
    const viewport = container.querySelector<HTMLElement>('#qr-viewport');
    if (viewport) {
      void startQrScanner(viewport, (result) => {
        // Auto-fill the URL and token from the QR code
        const urlInput = container.querySelector<HTMLInputElement>('#hub-url');
        const tokenInput = container.querySelector<HTMLInputElement>('#hub-token');
        if (urlInput) urlInput.value = result.url;
        if (tokenInput) tokenInput.value = result.token;
        // Switch to the URL tab and auto-connect if we have both
        const urlTab = container.querySelector<HTMLButtonElement>('[data-tab="url"]');
        urlTab?.click();
        if (result.url && result.token) {
          showStatus(container, 'QR scanned — connecting...');
          void connectHub(container, { name: result.url, url: result.url, token: result.token }, onConnected);
        } else {
          showStatus(container, 'QR scanned — please verify the details');
        }
      });
    }
  });

  // Seed phrase connect
  const btnSeed = container.querySelector<HTMLButtonElement>('#btn-connect-seed');
  btnSeed?.addEventListener('click', async () => {
    const textarea = container.querySelector<HTMLTextAreaElement>('#seed-phrase');
    const phrase = textarea?.value.trim() ?? '';
    const words = phrase.split(/\s+/);
    if (words.length !== 12) {
      showError(container, 'Seed phrase must be exactly 12 words');
      return;
    }
    await connectHub(
      container,
      { name: 'WireGuard Hub', url: '', seed_phrase: phrase, connection_type: 'wireguard' },
      onConnected,
    );
  });
}

async function connectHub(
  container: HTMLElement,
  payload: { name: string; url: string; token?: string; seed_phrase?: string; connection_type?: string },
  onConnected: () => void,
): Promise<void> {
  showStatus(container, 'Connecting...');
  hideError(container);
  try {
    await apiPost('/api/hubs/add', payload);
    showStatus(container, 'Connected!');
    setTimeout(onConnected, 500);
  } catch (e) {
    const msg = e instanceof Error ? e.message : 'Connection failed';
    showError(container, msg);
    hideStatus(container);
  }
}

function showError(container: HTMLElement, msg: string): void {
  const el = container.querySelector<HTMLElement>('#connect-error');
  if (el) {
    el.textContent = msg;
    el.classList.remove('hidden');
  }
}

function hideError(container: HTMLElement): void {
  container.querySelector('#connect-error')?.classList.add('hidden');
}

function showStatus(container: HTMLElement, msg: string): void {
  const el = container.querySelector<HTMLElement>('#connect-status');
  if (el) {
    el.textContent = msg;
    el.classList.remove('hidden');
  }
}

function hideStatus(container: HTMLElement): void {
  container.querySelector('#connect-status')?.classList.add('hidden');
}
